// @ts-strict-ignore
/* eslint-disable valid-typeof */
const round = (x) => Math.round(x * 100) / 100

export const CalculateLoanRepayment = ({ amount, apr, term }) => {
  const principal = amount
  const interest = apr / 100 / 12
  const payments = term * 12

  const x = Math.pow(1 + interest, payments)
  const monthly = (principal * x * interest) / (x - 1)

  if (
    !isNaN(monthly) &&
    monthly !== Number.POSITIVE_INFINITY &&
    monthly !== Number.NEGATIVE_INFINITY
  ) {
    const poundUKLocale = Intl.NumberFormat('en-UK')
    const monthlyPayment = poundUKLocale.format(round(monthly))
    const total = poundUKLocale.format(round(monthly * payments))
    const totalinterest = poundUKLocale.format(round(monthly * payments - principal))

    return {
      monthlyPayment,
      total,
      totalinterest,
    }
  }
}

const calculateMonthlyRateCreditCard = (monthlyInterest) => {
  let x = 0
  if (!monthlyInterest > 0) {
    return x
  }
  x = monthlyInterest / 100
  x = Math.pow(x + 1, 1 / 12)
  x = (x - 1) * 100
  return x >= 0 ? x : 0
}

export const calculateTotalInterestAndPeriod = (balance, apr, repayment) => {
  let totalInterest = 0
  let periods = 0
  let workingBalance
  let interestPayment
  const breakLoopPeriod = 1200
  const interestRate = calculateMonthlyRateCreditCard(apr) / 100
  workingBalance = balance
  interestPayment = balance * interestRate
  totalInterest += interestPayment
  while (workingBalance + interestPayment > repayment) {
    workingBalance = workingBalance - repayment + interestPayment
    interestPayment = workingBalance * interestRate
    totalInterest += interestPayment
    periods++
    if (periods > breakLoopPeriod) {
      return {
        error: 'The payment period is higher than 100 years.',
      }
    }
  }
  return {
    interest: Math.ceil(totalInterest),
    period: periods > 0 ? periods + 1 : 1,
    total: balance + Math.ceil(totalInterest),
  }
}

export const calculateMinimumRepayment = (balance, apr, minPercentage, minPayment) => {
  const monthlyInterestRate = calculateMonthlyRateCreditCard(apr)
  const minRepayment = Math.ceil(((minPercentage + monthlyInterestRate) * balance) / 100)
  return Math.max(minPayment, minRepayment)
}

const overpaymentTypes = {
  overBalance: 'number',
  overTermYears: 'number',
  overTermMonths: 'number',
  overInterest: 'number',
  overPaymentOne: 'number',
  overPaymentMonth: 'number',
  overpaymentType: 'string',
}

const throwTypeError = (expected, actual) => {
  throw new Error(`Invalid type expected type ${expected} but received type ${actual}`)
}
export const checkTypes = (value, calcType) => {
  Object.entries(value).map(([k, v]) =>
    typeof v === calcType[k] ? true : throwTypeError(calcType[k], typeof v),
  )
}

const numberOfPaymentPeriods = (rate, pmt, pv) => {
  // rate = interest rate
  // pmt = present monthly payment
  // pv = present value (balance)
  rate = parseFloat(rate)
  pmt = parseFloat(pmt)
  pv = parseFloat(pv)
  pv = -Math.abs(pv)
  let nperValue

  rate = rate / 12

  if (rate === 0) nperValue = -(0 + pv) / pmt
  else nperValue = Math.log((-0 * rate + pmt) / (pmt + rate * pv)) / Math.log(1 + rate)

  return nperValue
}

export const OverpaymentCalculations = (values) => {
  checkTypes(values, overpaymentTypes)

  let {
    overBalance = 0,
    overTermYears = 0,
    overTermMonths = 0,
    overInterest = 0,
    overpaymentType = 'Both',
    overPaymentOne = 0,
    overPaymentMonth = 0,
  } = values

  const overInterestPercantage = overInterest / 100

  const val1 = overBalance * (overInterestPercantage / 12)
  const val2 = 1 + overInterestPercantage / 12
  const val3 = -Math.abs(overTermYears * 12 + overTermMonths)
  const val4 = 1 - Math.pow(val2, val3)
  let monthlyPayment = val1 / val4

  const origMonths = numberOfPaymentPeriods(overInterestPercantage, monthlyPayment, overBalance)
  const origYears = origMonths / 12
  const origInterest = origMonths * monthlyPayment - overBalance

  if (overpaymentType === 'One-off') overBalance = overBalance - overPaymentOne
  else if (overpaymentType === 'Recurring') monthlyPayment += overPaymentMonth
  else if (overpaymentType === 'Both') {
    overBalance = overBalance - overPaymentOne
    monthlyPayment += overPaymentMonth
  }

  const refinedMonths = numberOfPaymentPeriods(overInterestPercantage, monthlyPayment, overBalance)
  const refinedYears = refinedMonths / 12
  const refinedInterest = refinedMonths * monthlyPayment - overBalance

  const timeDiff = origYears - refinedYears
  const overIntDiff = Math.round(origInterest - refinedInterest)

  const whole = Math.floor(timeDiff)
  const decimal = timeDiff - whole
  let decNum = decimal * 12
  decNum = Math.floor(decNum)

  let overTermDiff = whole + ' years'

  if (decNum !== 0) overTermDiff += ' and ' + decNum + ' months'
  return { origInterest, refinedInterest, overTermDiff, overIntDiff }
}

const monthlyInterestRate = (rate, monthsPerYear) => rate / monthsPerYear / 100

const multiplyExponent = (a, b) => a ** b

const calculateInterest = (monthlyInterest, totalNumberOfPayments) =>
  (monthlyInterest * multiplyExponent(1 + monthlyInterest, totalNumberOfPayments)) /
  (multiplyExponent(monthlyInterest + 1, totalNumberOfPayments) - 1)

const mortgageRepaymentTypes = {
  amountBorrowed: 'number',
  deposit: 'number',
  interestRate: 'number',
  mortgageTerm: 'number',
  repaymentType: 'string',
}

export const MortgageMonthlyCostRepayment = (values) => {
  checkTypes(values, mortgageRepaymentTypes)

  const {
    amountBorrowed = 0,
    deposit = 0,
    interestRate = 0,
    mortgageTerm = 0,
    repaymentType,
  } = values

  const monthlyInterest = monthlyInterestRate(interestRate, 12)
  const totalNumberOfPayments = mortgageTerm * 12
  const calcInterest = calculateInterest(monthlyInterest, totalNumberOfPayments)
  const repaymentMonthlyCost = (amountBorrowed - deposit) * calcInterest

  const interestOnlyCost = ((amountBorrowed - deposit) * interestRate) / 12 / 100

  const mortgageAmount = amountBorrowed - deposit

  if (repaymentType === 'repayment') {
    return {
      monthly: repaymentMonthlyCost,
      mortgageAmount: mortgageAmount,
      interest: interestRate,
      totalRepayment: totalNumberOfPayments * repaymentMonthlyCost,
      term: mortgageTerm,
      type: 'repayment',
    }
  } else {
    return {
      monthly: interestOnlyCost,
      mortgageAmount: mortgageAmount,
      interest: interestRate,
      interestCost: totalNumberOfPayments * interestOnlyCost,
      totalRepayment: totalNumberOfPayments * interestOnlyCost + (amountBorrowed - deposit),
      term: mortgageTerm,
      type: 'interest-only',
    }
  }
}
